import { createLogger, serializeError } from 'logging';
import { useEffect, useState } from 'react';

import { useCheckoutContext } from './CheckoutContext';
import { handleCheckoutPreview } from './actions';

const logger = createLogger('usePurchasePreview');

export function usePurchasePreview(): {
    currency: string;
    currencySymbol: string;
    subtotal: number;
    tax: number;
    discount?: number;
    total: number;
} {
    const { currency, currencySymbol, setCurrency, planCode, promoCode, postalCode, countryCode } =
        useCheckoutContext();
    const [subtotal, setSubtotal] = useState<number>(0);
    const [tax, setTax] = useState<number>(0);
    const [discount, setDiscount] = useState<number>();
    const [total, setTotal] = useState<number>(0);

    useEffect(() => {
        // Prevent infinite loops by not making API calls if required data is missing
        if (!planCode || !countryCode) {
            return;
        }

        // Context automatically returns undefined for promoCode when feature is disabled
        const requestParams = {
            planCode,
            ...(promoCode && { promoCode }),
            currencyCode: currency,
            postalCode: postalCode ?? '',
            country: countryCode,
        };

        handleCheckoutPreview(requestParams).then(
            (result) => {
                if (!result) {
                    logger.error({ planCode }, 'Preview result is undefined');
                    return;
                }
                // Only update currency if it's actually different to prevent loops
                if (result.currency !== currency) {
                    setCurrency(result.currency);
                }
                setSubtotal(result.subtotal ?? 0);
                setTax(result.tax ?? 0);
                setDiscount(result.discount);
                setTotal(result.total ?? 0);
            },
            (error) => {
                logger.error({ error: serializeError(error) }, 'Error in hook to preview taxes');
            },
        );
    }, [planCode, promoCode, postalCode, countryCode, currency, setCurrency]);

    return {
        currency,
        currencySymbol,
        subtotal,
        tax,
        discount,
        total,
    };
}
